import { CookieName } from '@/config/cookies'
import BaseAPIService from './BaseAPIService'
import {
  BookingResponse,
  BookingSummary,
  TravellerSummary,
  UpdateTravellersResponse,
} from './BookingService.interface'
import endpoints from '@/config/endpoints'
import Cookies from 'js-cookie'

class BookingService extends BaseAPIService {
  constructor() {
    super(endpoints.bookingCustomer) // Use "bookingCustomer" key from endpoints
  }

  /**
   * Fetch bookings for a specific user.
   */
  public async getBookings(
    userEmail: string,
    page = 1,
    pageSize = 400
  ): Promise<BookingSummary[]> {
    try {
      const url = `/booking/user`
      const response = await this.apiClient.get<BookingResponse>(url, {
        headers: this.getDefaultHeaders(),
        params: {
          email: userEmail,
          includeUser: true,
          includeOptions: true,
          includeTravellers: true,
          includePayments: true,
          page,
          pageSize,
        },
      })

      // Return the bookings directly
      return response.data.message.bookings
    } catch (error) {
      console.error('Error fetching bookings:', error)
      throw error
    }
  }

  public async updateTravellers(
    updates: TravellerSummary[],
    bookingReference: string
  ): Promise<UpdateTravellersResponse> {
    try {
      const url = `/booking/travellers`
      const { data } = await this.apiClient.put<UpdateTravellersResponse>(
        url,
        { travellers: updates, bookingReference },
        {
          headers: this.getDefaultHeaders(),
        }
      )
      return data
    } catch (error) {
      console.error('Error fetching bookings:', error)
      throw error
    }
  }

  /**
   * Overrides the default headers with additional customizations.
   * This method retrieves the user ID token from cookies, throws an error if the token is missing,
   * removes unused headers, and adds an `Authorization` header with the token.
   *
   * @protected
   * @override
   * @returns {{ [key: string]: string }} The customized headers object.
   * @throws {Error} If the user ID token is missing from the cookies.
   */

  protected override getDefaultHeaders(): { [key: string]: string } {
    // Retrieve the user ID token from cookies
    const idToken = Cookies.get(CookieName.USER_ID_TOKEN)

    // Throw an error if the token is missing
    if (!idToken) {
      throw new Error('idToken is missing')
    }

    // Call the parent method to get the default headers
    const headers = super.getDefaultHeaders()

    // Get the locale using this.getLocale
    const acceptLanguage = this.getLegacyLocale()

    // Remove unused headers
    delete headers.locale

    // Return the customized headers
    return {
      ...headers,
      Authorization: `Bearer ${idToken}`, // Add Authorization header with the token
      'Accept-Language': acceptLanguage, // Use Accept-Language from this.getLocale
    }
  }
}

export default BookingService
